import { useState, useMemo } from "react";

export interface PickupTimeFilter {
  startTime: string;
  endTime: string;
}

const STORAGE_KEY = "pickupTimeFilter";
const OPENED_STORAGE_KEY = "pickupTimeFilterOpened";
const DEFAULT_START_TIME = "17:00";
const DEFAULT_END_TIME = "22:00";

/** 지도 등 리스트 외 화면에서 로컬스토리지와 동기화할 때 사용 */
export function readPickupTimeFilterFromStorage(): PickupTimeFilter | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved) as PickupTimeFilter;
    }
  } catch (error) {
    console.error("Failed to load pickup time filter:", error);
  }
  return null;
}

// localStorage에 필터 저장
function saveFilterToStorage(filter: PickupTimeFilter | null): void {
  if (filter) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filter));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

// localStorage에서 열어본 적이 있는지 확인
function hasOpenedFilterFromStorage(): boolean {
  return localStorage.getItem(OPENED_STORAGE_KEY) === "true";
}

// 열어본 적이 있다고 표시
function markFilterAsOpened(): void {
  localStorage.setItem(OPENED_STORAGE_KEY, "true");
}

// 초기 시간 가져오기 (필터가 없을 때 사용)
function getInitialTimes(): { startTime: string; endTime: string } {
  const savedFilter = readPickupTimeFilterFromStorage();
  return {
    startTime: savedFilter?.startTime || DEFAULT_START_TIME,
    endTime: savedFilter?.endTime || DEFAULT_END_TIME,
  };
}

// **************************************************************
// ⭐ [픽업 시간 필터 관리 훅]
// **************************************************************
export const usePickupTimeFilter = () => {
  const [pickupTimeFilter, setPickupTimeFilterState] =
    useState<PickupTimeFilter | null>(() => readPickupTimeFilterFromStorage());

  const [hasOpenedPickupTimeFilter, setHasOpenedPickupTimeFilterState] =
    useState(hasOpenedFilterFromStorage);

  // 필터 적용 함수
  const applyFilter = (startTime: string, endTime: string): void => {
    const filter = { startTime, endTime };
    setPickupTimeFilterState(filter);
    saveFilterToStorage(filter);
  };

  // 필터 초기화 함수
  const resetFilter = (): void => {
    setPickupTimeFilterState(null);
    saveFilterToStorage(null);
  };

  // 필터 열기 처리 (처음 열었을 때만 플래그 저장)
  const handleFilterOpen = (): void => {
    if (!hasOpenedPickupTimeFilter) {
      markFilterAsOpened();
      setHasOpenedPickupTimeFilterState(true);
    }
  };

  // 초기 시간 계산 (바텀시트에서 사용)
  const initialTimes = useMemo(() => getInitialTimes(), []);

  return {
    pickupTimeFilter,
    applyFilter,
    resetFilter,
    hasOpenedPickupTimeFilter,
    handleFilterOpen,
    initialStartTime: pickupTimeFilter?.startTime || initialTimes.startTime,
    initialEndTime: pickupTimeFilter?.endTime || initialTimes.endTime,
    hasFilter: !!pickupTimeFilter,
  };
};
